import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";

const PAGE_SIZE = 10;

function resultColor(result) {
  if (result === "Win") return "text-lime-400";
  if (result === "Draw") return "text-yellow-300";
  return "text-pink-400";
}
function fmt(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function GameHistoryList({ user }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [firstDoc, setFirstDoc] = useState(null);
  const [pageStack, setPageStack] = useState([]); // To support going back
  const [hasNext, setHasNext] = useState(false);
  const [page, setPage] = useState(1);

  // Load first page
  useEffect(() => {
    if (!user?.uid) return;
    setLoading(true);
    const q = query(
      collection(db, "games"),
      where("players", "array-contains", user.uid),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );
    getDocs(q).then((snap) => {
      const items = [];
      snap.forEach((doc) => {
        const g = doc.data();
        const isX = g.playerX?.userId === user.uid;
        const opp = isX
          ? g.playerO?.username || "Opponent"
          : g.playerX?.username || "Opponent";
        let result = "Lose";
        if (g.winner === user.uid) result = "Win";
        else if (g.winner === "draw") result = "Draw";
        items.push({
          date: fmt(g.createdAt),
          opponent: opp,
          bet: g.betAmount || 0,
          result,
        });
      });
      setGames(items);
      setFirstDoc(snap.docs[0]);
      setLastDoc(snap.docs[snap.docs.length - 1]);
      setPageStack([]); // Reset page stack
      setPage(1);
      setHasNext(snap.docs.length === PAGE_SIZE);
      setLoading(false);
    });
  }, [user?.uid]);

  // Next page
  const handleNext = async () => {
    if (!lastDoc) return;
    setLoading(true);
    const q = query(
      collection(db, "games"),
      where("players", "array-contains", user.uid),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );
    const snap = await getDocs(q);
    const items = [];
    snap.forEach((doc) => {
      const g = doc.data();
      const isX = g.playerX?.userId === user.uid;
      const opp = isX
        ? g.playerO?.username || "Opponent"
        : g.playerX?.username || "Opponent";
      let result = "Lose";
      if (g.winner === user.uid) result = "Win";
      else if (g.winner === "draw") result = "Draw";
      items.push({
        date: fmt(g.createdAt),
        opponent: opp,
        bet: g.betAmount || 0,
        result,
      });
    });
    setPageStack((prev) => [...prev, firstDoc]);
    setFirstDoc(snap.docs[0]);
    setLastDoc(snap.docs[snap.docs.length - 1]);
    setGames(items);
    setPage((p) => p + 1);
    setHasNext(snap.docs.length === PAGE_SIZE);
    setLoading(false);
  };

  // Previous page
  const handlePrev = async () => {
    if (pageStack.length === 0) return;
    setLoading(true);
    const prevDoc = pageStack[pageStack.length - 1];
    const q = query(
      collection(db, "games"),
      where("players", "array-contains", user.uid),
      orderBy("createdAt", "desc"),
      startAfter(prevDoc),
      limit(PAGE_SIZE)
    );
    const snap = await getDocs(q);
    const items = [];
    snap.forEach((doc) => {
      const g = doc.data();
      const isX = g.playerX?.userId === user.uid;
      const opp = isX
        ? g.playerO?.username || "Opponent"
        : g.playerX?.username || "Opponent";
      let result = "Lose";
      if (g.winner === user.uid) result = "Win";
      else if (g.winner === "draw") result = "Draw";
      items.push({
        date: fmt(g.createdAt),
        opponent: opp,
        bet: g.betAmount || 0,
        result,
      });
    });
    setPageStack((prev) => prev.slice(0, prev.length - 1));
    setFirstDoc(snap.docs[0]);
    setLastDoc(snap.docs[snap.docs.length - 1]);
    setGames(items);
    setPage((p) => p - 1);
    setHasNext(snap.docs.length === PAGE_SIZE);
    setLoading(false);
  };

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 mb-20 bg-slate-900 border-2 border-lime-300/10 rounded-2xl shadow-lg p-6">
      <div className="text-lime-300 font-bold text-xl mb-4 flex items-center gap-2">
        <span>🕑</span> XO Game History
      </div>
      <div className="grid grid-cols-12 font-bold text-lime-400 text-xs md:text-sm border-b border-lime-900/30 pb-1 mb-2">
        <div className="col-span-4">Date</div>
        <div className="col-span-4">Opponent</div>
        <div className="col-span-2 text-right">Bet</div>
        <div className="col-span-2 text-center">Result</div>
      </div>
      {loading ? (
        <div className="text-slate-400 text-center py-8">Loading…</div>
      ) : games.length === 0 ? (
        <div className="text-slate-400 text-center py-4">
          No games played yet.
        </div>
      ) : (
        games.map((g, i) => (
          <div
            key={i}
            className={`grid grid-cols-12 items-center py-2 border-b border-slate-700 last:border-0 text-white`}
          >
            <div className="col-span-4 text-xs">{g.date}</div>
            <div className="col-span-4">{g.opponent}</div>
            <div className="col-span-2 text-right text-lime-200 font-bold">
              {g.bet} USDT
            </div>
            <div
              className={`col-span-2 text-center font-extrabold ${resultColor(
                g.result
              )}`}
            >
              {g.result}
            </div>
          </div>
        ))
      )}
      {/* Pagination controls */}
      <div className="flex justify-between mt-4">
        <button
          className="bg-slate-700 text-lime-300 px-4 py-1 rounded-xl shadow font-bold disabled:opacity-40"
          onClick={handlePrev}
          disabled={pageStack.length === 0 || loading}
        >
          Previous
        </button>
        <div className="text-slate-400 text-sm mt-2">Page {page}</div>
        <button
          className="bg-slate-700 text-lime-300 px-4 py-1 rounded-xl shadow font-bold disabled:opacity-40"
          onClick={handleNext}
          disabled={!hasNext || loading}
        >
          Next
        </button>
      </div>
    </div>
  );
}
